import {
  HttpClient,
  HttpErrorResponse,
  HttpHeaders,
  HttpParams,
} from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable, Subject } from "rxjs";
import { map, take } from "rxjs/operators";
import Diagnosis from "../../models/patient-chart/diagnosis";
import { IcdRequest } from "../../pages/patient-chart/diagnosis/models/diagnosis.dto";
import Page from "../../pages/patient-chart/diagnosis/models/page";
import { ConfigurationService } from "../configuration/configuration.service";

@Injectable()
export class DiagnosisService {
  chartUrl: string;
  headers: HttpHeaders;
  diagnosisData$: Subject<Array<Diagnosis>> = new Subject<Array<Diagnosis>>();

  constructor(private http: HttpClient, private config: ConfigurationService) {
    this.headers = new HttpHeaders().set("Content-Type", "application/json");

    this.chartUrl = this.config.resolvePatientChartsUrl();
  }

  getDiagnosisData(id): Observable<Array<Diagnosis>> {
    let lookupUrl = `${this.chartUrl}/patient-charts/${id}/diagnoses?by=current`;
    this.http
      .get<Page>(lookupUrl, { headers: this.headers })
      .pipe(
        map((page) => page.content),
        take(1)
      )
      .subscribe((diagnosisList) => {
        if (diagnosisList == null) {
          diagnosisList = new Array<Diagnosis>();
        }
        this.diagnosisData$.next(diagnosisList);
      });

    return this.diagnosisData$.asObservable().share<Array<Diagnosis>>();
  }

  getDiagnosesData(id) {
    let diagnosesUrl = `${this.chartUrl}/patient-charts/${id}/diagnoses?by=current`;
    return this.http
      .get<Page>(diagnosesUrl, { headers: this.headers })
      .map((page) => page.content)
      .pipe(take(1));
  }

  getDiagnosisHistoryData(
    patientChartId,
    currentPage,
    pageLimit,
    orderBy,
    sortBy,
    isPalPatientAndIsPal1_5
  ) {
    let lookupUrl = `${this.chartUrl}/patient-charts/${patientChartId}/diagnoses?page=${currentPage}&limit=${pageLimit}&direction=${orderBy}&sortBy=${sortBy}&pal15Toggle=${isPalPatientAndIsPal1_5}`;
    return this.http
      .get<Page>(lookupUrl, { headers: this.headers })
      .pipe(take(1));
  }

  putDiagnosisData(
    id,
    diagnosisiCodeData: Array<Diagnosis>
  ): Observable<Array<Diagnosis>> {
    let url: string = `${this.chartUrl}/patient-charts/${id}/diagnoses`;

    return this.http
      .put<Array<Diagnosis>>(url, diagnosisiCodeData, {
        headers: this.headers,
      })
      .pipe(take(1));
  }

  deleteDiagnosis(diagnosisId, patientChartId: string) {
    let url: string = `${this.chartUrl}/patient-charts/${patientChartId}/diagnoses/${diagnosisId}`;
    return new Observable((observer) => {
      this.http.delete(url, { headers: this.headers }).subscribe(
        (data) => {
          observer.next({ data, patientChartId });
          observer.complete();
        },
        (err: HttpErrorResponse) => {
          observer.error(err);
          observer.complete();
        }
      );
    });
  }

  putDiagnosisDataForVisit(
    id,
    diagnosisiCodeData: Array<Diagnosis>
  ): Observable<Array<Diagnosis>> {
    let url: string = `${this.chartUrl}/patient-charts/${id}/diagnoses/visits?pal15Toggle=true`;
    return this.http
      .put<Array<Diagnosis>>(url, diagnosisiCodeData, {
        headers: this.headers,
      })
      .pipe(take(1));
  }

  updateVisitDiagnosesDateTime(
    patientChartId: number,
    assessmentId: number,
    newVisitStartDateTime: string,
    newVisitEndDateTime: string
  ): Observable<Array<Diagnosis>> {
    let url: string = `${this.chartUrl}/patient-charts/${patientChartId}/diagnoses/updateVisitDiagnosesTimes/assessment/${assessmentId}`;
    return this.http
      .put<Array<Diagnosis>>(url, {
        newVisitStartDateTime,
        newVisitEndDateTime,
      })
      .pipe(take(1));
  }

  putConfirmDiagnosesStatus(
    patientChartId: number,
    assessmentId: number,
    startDate: string = "",
    endDate: string = ""
  ): Observable<Array<Diagnosis>> {
    let url: string = `${this.chartUrl}/patient-charts/${patientChartId}/diagnoses/confirm-visit-diagnosis`;
    let params = new HttpParams()
      .set("assessmentId", assessmentId.toString())
      .set("assessmentStartDate", startDate)
      .set("assessmentEndDate", endDate);

    return this.http
      .put<Array<Diagnosis>>(
        url,
        {
          startDate,
          endDate,
        },
        {
          headers: this.headers,
          params,
        }
      )
      .pipe(take(1));
  }

  getDiagnosisDataForVisit(id): Observable<Array<Diagnosis>> {
    let lookupUrl = `${this.chartUrl}/patient-charts/${id}/diagnoses?by=current&pal15Toggle=true`;

    this.http
      .get<Page>(lookupUrl, { headers: this.headers })
      .pipe(
        map((page) => page.content),
        take(1)
      )
      .subscribe((diagnosisList) => {
        if (diagnosisList == null) {
          diagnosisList = new Array<Diagnosis>();
        }
        this.diagnosisData$.next(diagnosisList);
      });

    return this.diagnosisData$.asObservable().share<Array<Diagnosis>>();
  }

  postCheckDuplicateDiagnoses(
    patientChartId: number,
    duplicateCheckList: Array<IcdRequest>,
    checkDB: boolean
  ): Observable<boolean> {
    let url: string = `${this.chartUrl}/patient-charts/${patientChartId}/diagnoses/duplicate-diagnosis?checkDB=${checkDB}`;

    return this.http.post<boolean>(url, duplicateCheckList, {
      headers: this.headers,
    });
  }
}



// WEBPACK FOOTER //
// ./src/providers/patient-charts/diagnosis-service.ts